import { Link } from "react-router-dom";
import {
  ArrowUpRight, Sparkles, Target, Users, Radar,
  ShieldCheck, Brain, Zap, TrendingUp, Layers
} from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";
import ProductCard from "../../components/marketing/ProductCard";

export default function HomePage() {
  return (
    <MarketingLayout>
      {/* ─── HERO ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Abstract background */}
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-blue-100 dark:bg-blue-950/40 blur-3xl opacity-40" />
          <div
            className="absolute inset-0 opacity-[0.035] dark:opacity-[0.08]"
            style={{
              backgroundImage: "linear-gradient(rgba(15,23,42,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.7) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
              maskImage: "radial-gradient(ellipse 90% 60% at 50% 30%, black 40%, transparent 70%)",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-24 pb-24 md:pt-32 md:pb-28">
          <h1 data-testid="hero-headline" className="font-outfit text-center text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-slate-900 dark:text-white leading-[1.05] max-w-5xl mx-auto">
            Intelligence for Organizations
            <br />
            <span className="bg-gradient-to-r from-slate-900 via-blue-800 to-slate-900 dark:from-white dark:via-blue-300 dark:to-white bg-clip-text text-transparent">
              That Never Stop Improving.
            </span>
          </h1>

          <p data-testid="hero-subhead" className="mt-8 max-w-2xl mx-auto text-center font-ibm-plex text-lg md:text-xl leading-relaxed text-slate-600 dark:text-slate-400">
            SYNQ transforms projects, leadership, teams and organizations into actionable intelligence —
            helping leaders make better decisions with confidence.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/products"
              data-testid="hero-explore-products-btn"
              className="inline-flex items-center gap-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px] hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
            >
              Explore Products <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact?intent=demo"
              data-testid="hero-request-demo-btn"
              className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px] hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
            >
              Request Demo
            </Link>
          </div>

          {/* Trust signals band */}
          <div className="mt-16 pt-8 border-t border-slate-200/60 dark:border-slate-800/60">
            <p className="text-center font-ibm-plex text-[11px] uppercase tracking-widest text-slate-500 mb-5">
              Built for enterprise-grade decision-making
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { Icon: ShieldCheck, label: "Anonymized by design"    },
                { Icon: Brain,       label: "AI-powered intelligence" },
                { Icon: Radar,       label: "Adaptive sensing"        },
                { Icon: Layers,      label: "Multi-layer signal"      },
              ].map(({ Icon, label }, i) => (
                <div key={i} className="flex items-center gap-2.5 justify-center opacity-70">
                  <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                  <span className="font-ibm-plex text-sm text-slate-500 dark:text-slate-400">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHAT IS SYNQ ────────────────────────────────────────────────── */}
      <section data-testid="what-is-synq" className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-24">
          <div className="grid md:grid-cols-3 gap-10 md:gap-16 items-start">
            <div>
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">
                01 · The Platform
              </div>
              <h2 className="font-outfit text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
                What is SYNQ?
              </h2>
            </div>
            <div className="md:col-span-2 space-y-5">
              <p className="font-ibm-plex text-xl leading-relaxed text-slate-800 dark:text-slate-200">
                SYNQ is an enterprise intelligence platform designed to help organizations
                continuously understand, improve and evolve.
              </p>
              <p className="font-ibm-plex text-base leading-relaxed text-slate-600 dark:text-slate-400 max-w-3xl">
                Instead of isolated dashboards and disconnected reports, SYNQ provides
                <span className="text-slate-900 dark:text-white font-semibold"> intelligence that guides action</span> —
                across projects, leadership, teams and the entire organization.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4 max-w-lg">
                {[
                  { n: "7",   label: "Health Pillars" },
                  { n: "Live",label: "Adaptive Sensing" },
                  { n: "AI",  label: "Recommendations" },
                  { n: "0",   label: "PII Exposed" },
                ].map((s, i) => (
                  <div key={i} className="border-l-2 border-slate-900 dark:border-white pl-4">
                    <div className="font-outfit text-2xl font-black text-slate-900 dark:text-white">{s.n}</div>
                    <div className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRODUCTS SECTION ────────────────────────────────────────────── */}
      <section data-testid="products-section" className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">
                02 · The Ecosystem
              </div>
              <h2 className="font-outfit text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
                Intelligence Platforms
              </h2>
              <p className="font-ibm-plex text-base text-slate-600 dark:text-slate-400 mt-3 max-w-xl">
                Every product in the SYNQ ecosystem is built to answer one question:
                <span className="text-slate-900 dark:text-white font-semibold"> what should we do next?</span>
              </p>
            </div>
            <Link
              to="/products"
              className="hidden md:inline-flex items-center gap-1.5 text-sm font-ibm-plex font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            >
              View all products <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <ProductCard
              name="SYNQ Projects"
              subtitle="Project Intelligence Platform"
              description="Predict project health, delivery confidence, execution quality and organizational capability across every project."
              to="/products/projects"
              accentClass="bg-blue-200 dark:bg-blue-900/50"
              badge="Live"
              icon={<Target className="w-5 h-5" />}
            />
            <ProductCard
              name="SYNQ Evolve"
              subtitle="Organizational Intelligence Platform"
              description="Understand leadership, team dynamics, culture and organizational intelligence — continuous improvement, guided by AI."
              to="/products/evolve"
              accentClass="bg-emerald-200 dark:bg-emerald-900/50"
              badge="Live"
              icon={<TrendingUp className="w-5 h-5" />}
            />
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-5">
            <ProductCard
              name="SYNQ AI"
              subtitle="Applied AI Intelligence"
              description="Bring bespoke, domain-aware AI advisors directly into your organization's workflows."
              to="#"
              accentClass="bg-purple-200 dark:bg-purple-900/50"
              badge="Coming Soon"
              icon={<Brain className="w-5 h-5" />}
            />
            <ProductCard
              name="SYNQ Strategy"
              subtitle="Strategic Intelligence"
              description="Turn strategic intent into measurable, tracked outcomes with real-time strategic sensing."
              to="#"
              accentClass="bg-amber-200 dark:bg-amber-900/50"
              badge="Coming Soon"
              icon={<Zap className="w-5 h-5" />}
            />
            <ProductCard
              name="SYNQ Risk"
              subtitle="Risk Intelligence"
              description="Detect and pre-empt organizational, operational and delivery risk — before it surfaces."
              to="#"
              accentClass="bg-rose-200 dark:bg-rose-900/50"
              badge="Coming Soon"
              icon={<ShieldCheck className="w-5 h-5" />}
            />
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ────────────────────────────────────────────────── */}
      <section data-testid="how-it-works" className="border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-3">
              03 · How SYNQ Works
            </div>
            <h2 className="font-outfit text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight">
              Sense. Understand. Act.
            </h2>
            <p className="font-ibm-plex text-base text-slate-600 dark:text-slate-400 mt-4">
              Not another dashboard. SYNQ is a live intelligence layer for the modern organization.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                num: "01",
                title: "Sense",
                text: "SYNQ captures signals from projects, leadership, teams and open feedback — adaptive, fatigue-aware, and anonymized by design.",
                Icon: Radar,
              },
              {
                num: "02",
                title: "Understand",
                text: "Reality-gap engines, signal-confidence weighting and pattern detection turn raw signal into narrative you can act on.",
                Icon: Brain,
              },
              {
                num: "03",
                title: "Act",
                text: "Every intelligence layer surfaces systemic — never personal — stabilization actions. Track, iterate, and evolve.",
                Icon: Zap,
              },
            ].map((s, i) => (
              <div key={i} className="relative border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-2xl p-7">
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center">
                    <s.Icon className="w-5 h-5" />
                  </div>
                  <span className="font-outfit text-3xl font-black text-slate-200 dark:text-slate-800">{s.num}</span>
                </div>
                <h3 className="font-outfit font-black text-xl text-slate-900 dark:text-white">{s.title}</h3>
                <p className="font-ibm-plex text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA BAND ────────────────────────────────────────────────────── */}
      <section data-testid="home-cta" className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-24">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 p-10 md:p-14">
            <div aria-hidden className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-blue-500/30 dark:bg-blue-200/50 blur-3xl" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="font-outfit text-3xl md:text-4xl font-black leading-tight">
                  See what SYNQ can uncover in your organization.
                </h2>
                <p className="font-ibm-plex text-slate-300 dark:text-slate-600 mt-3 leading-relaxed">
                  Book a 30-minute private walkthrough with our team. No slides — a live intelligence session on your context.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/contact?intent=demo"
                  data-testid="cta-request-demo"
                  className="inline-flex items-center gap-2 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-6 py-3.5 rounded-full font-outfit font-semibold text-[15px] hover:opacity-90 transition-opacity"
                >
                  Request Demo <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
